import Button from "@/Components/Button/Button";
import { CheckboxProps } from "@/Components/Checkbox/Checkbox";
import ProgressBar from "@/Components/Progress/Progress";
import { useSetFlyItem } from "@/shared/atoms/useFlyItem";
import useBranchId from "@/shared/hooks/useBranchId";
import {
  useAddCartItem,
  useCart,
  useUpdateCartItemByIndex,
} from "@/shared/hooks/useCartHook";
import useCommon from "@/shared/hooks/useCommon";
import useSearchParams from "@/shared/hooks/useSearchParams";
import { cn } from "@/shared/utils/cn";
import {
  formatPrice,
  getFieldByLang,
  withCloudinaryBackgroundRemoval,
} from "@/shared/utils/helpers";
import { CartItem } from "@/shared/utils/interfaces";
import {
  ItemImageAndTextPropertiesEnum,
  ToppingCategory,
} from "@/shared/v2/queries/interfacesV2";
import { motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import "swiper/css";
import { Swiper, SwiperSlide } from "swiper/react";
import EmptyImage from "../../../App/Assets/Images/empty.png";
import Kiosk from "./Kiosk";
import {
  resolveToppingImageUrl,
  toppingCategoryHasImage,
} from "./kioskImageUtils";
import useKioskProduct from "./useKioskProduct";

const priceFormat = (price: number) =>
  price > 0 ? ` ( +${formatPrice(price)})` : ` ( -${formatPrice(price * -1)})`;

// tall, bold nav buttons for easier touch on the kiosk
const kioskNavButtonClass =
  "min-h-[9rem] text-[3rem] font-bold border-4 touch-manipulation";

const ToppingChoice = ({
  garnish,
  index,
  update,
  values,
}: {
  garnish: ToppingCategory;
  index: number;
  update: Function;
  values: any;
}) => {
  const branch = useBranchId();
  const hasImage = toppingCategoryHasImage(
    garnish?.item_image_and_text_properties
  );
  const showText =
    garnish?.item_image_and_text_properties !==
    ItemImageAndTextPropertiesEnum.only_image;
  const isOptional = garnish.topping_content_type === 2;
  const isMultipleChoice = garnish.topping_content_type === 3 || hasImage;
  const [hiddenImageIds, setHiddenImageIds] = useState<Record<string, boolean>>(
    {}
  );

  const scrollTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(scrollTimeout.current), []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    el.classList.add("is-scrolling");
    clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(
      () => el.classList.remove("is-scrolling"),
      800
    );
  };

  const value = values?.[garnish.item_id] || [];

  const isSelected = (topping) =>
    value.some((i) => i.item_id === topping.item_id);

  const handleClick = (topping) => {
    update((prev) => {
      const current = prev[garnish.item_id] || [];
      const exists = current.some((i) => i.item_id === topping.item_id);

      // MULTIPLE (0..n)
      if (isMultipleChoice) {
        return {
          ...prev,
          [garnish.item_id]: exists
            ? current.filter((i) => i.item_id !== topping.item_id)
            : [...current, topping],
        };
      }

      // OPTIONAL (0..1)
      if (isOptional) {
        return {
          ...prev,
          [garnish.item_id]: exists ? [] : [topping],
        };
      }

      // SINGLE (exactly 1)
      return {
        ...prev,
        [garnish.item_id]: [topping],
      };
    });
  };

  return (
    <div
      onScroll={handleScroll}
      className="kiosk-scroll mt-[1rem] min-h-0 w-full overflow-x-hidden overflow-y-auto overscroll-contain"
    >
      <div
        className={cn(
          // padding leaves room for the scale animation so it never overflows
          "w-full gap-[1rem] p-[1rem]",
          isMultipleChoice
            ? "grid grid-cols-[repeat(auto-fit,minmax(23rem,1fr))]"
            : "flex flex-col"
        )}
      >
        {garnish.sub_content?.map((topping) => {
          const selected = isSelected(topping);
          const imageUrl = resolveToppingImageUrl(
            topping.image_cloudinary_id,
            branch
          );
          const showImage =
            hasImage && imageUrl !== null && !hiddenImageIds[topping.item_id];

          return (
            <motion.div
              className={cn(
                "self-center flex w-full min-w-0 cursor-pointer items-center justify-center touch-manipulation",
                showImage ? "text-4xl" : "text-6xl",
                isMultipleChoice
                  ? "min-h-120 flex-col p-[2rem] text-center"
                  : "flex-row px-6 py-8 text-left",
                selected
                  ? "bg-(--button_background_color) text-(--button_text_color)"
                  : "bg-white text-text",
                "border border-(--button_background_color)"
              )}
              onClick={() => handleClick(topping)}
              key={topping.item_id}
              whileTap={{ scale: 0.94 }}
              animate={selected ? "selected" : "idle"}
              initial={false}
              variants={{
                idle: { scale: 1 },
                selected: {
                  scale: [1, 1.05, 1],
                  transition: { duration: 0.28, ease: "easeOut" },
                },
              }}
              transition={{ type: "spring", stiffness: 420, damping: 24 }}
            >
              {showImage && (
                <img
                  src={imageUrl}
                  onError={() =>
                    setHiddenImageIds((prev) => ({
                      ...prev,
                      [topping.item_id]: true,
                    }))
                  }
                  className={cn(
                    "h-[28rem] w-56 object-contain",
                    isMultipleChoice ? "mb-[1.5rem]" : "mb-0"
                  )}
                />
              )}
              {showText && (
                <div className="max-w-full wrap-break-word font-black">
                  {getFieldByLang(topping.name)}{" "}
                </div>
              )}
              <div
                className={cn(
                  "text-btn-tertiary",
                  isMultipleChoice ? "ml-0" : "ml-auto"
                )}
              >
                {parseFloat(topping.price) > 0 &&
                  priceFormat(parseFloat(topping.price))}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

const KioskProductDescription = ({
  product,
  description,
  price,
}: {
  product: any;
  description: string;
  price: number;
}) => {
  return (
    <div className="flex items-start gap-[clamp(1rem,2vw,3rem)]">
      <div className="shrink-0">
        <img
          src={withCloudinaryBackgroundRemoval(product?.image_cloudinary_id)}
          onError={(e: any) => (e.target.src = EmptyImage)}
          className="mx-auto h-auto w-[clamp(6rem,10vw,18rem)] object-contain"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div
          className="my-[clamp(0.5rem,1vh,1rem)] text-[clamp(1.5rem,3vw,4rem)]"
          style={{ fontWeight: "900" }}
        >
          {product?.name}
        </div>
        <div className="text-[clamp(1.25rem,2.5vw,3rem)]">{description}</div>
      </div>
      <div className="ml-auto shrink-0">
        <div
          className="text-[clamp(1.5rem,3vw,4rem)]"
          style={{ fontWeight: "900" }}
        >
          {formatPrice(price)}
        </div>
      </div>
    </div>
  );
};

const KioskProductDetail = () => {
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const increaseStep = () => {
    swiperRef?.current?.slideNext();
  };

  const decreaseStep = () => {
    swiperRef?.current?.slidePrev();
  };

  const setFlyItem = useSetFlyItem();
  const common = useCommon();
  const [cart] = useCart();
  const addCartItem = useAddCartItem();
  const updateCartItem = useUpdateCartItemByIndex();
  const history = useHistory();

  const product = useKioskProduct();
  const { categoryId } = useParams<{ categoryId: string }>();
  const toppingsCats = product?.sub_content?.filter(
    (cat) => cat.sub_content.length !== 0
  );
  const steps = toppingsCats?.length ?? 0;
  const params = useSearchParams();
  const defaultItemId = params?.get("index");
  const cartItem = cart?.[defaultItemId] ?? undefined;

  const cartIndex =
    defaultItemId === null ? undefined : parseInt(defaultItemId);

  const branch = useBranchId();
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(1);
  const [selectedToppings, setSelectedToppings] = useState<CheckboxProps>({});
  useEffect(() => {
    if (cartItem && cartIndex !== undefined) {
      setQuantity(cartItem.quantity);
      const converted = cart?.[cartIndex]?.sub_content.reduce(
        (acc: CheckboxProps, item) => {
          const key = String(item.parent_id);

          if (!acc[key]) {
            acc[key] = [];
          }

          acc[key].push({
            ...item,
            sub_content: [],
          });

          return acc;
        },
        {}
      );
      setSelectedToppings(converted);
    }
  }, [cartItem, cartIndex]);

  const isDisabled = () => {
    const garnish = toppingsCats?.[activeIndex];
    if (garnish) {
      const isRequired =
        garnish.topping_content_type === 1 &&
        selectedToppings[garnish.item_id] === undefined;
      return isRequired;
    }

    return true;
  };

  const onPrevClick = () => {
    if (activeIndex === 0) {
      return history.goBack();
    }
    decreaseStep();
  };

  const onSubmit = () => {
    const subContent = Object.keys(selectedToppings)?.map((key) => {
      return selectedToppings[key].map((item) => ({
        name: item?.name,
        price: item?.price,
        item_id: item?.item_id,
        quantity: quantity,
        parent_id: +key,
        vat_percentage: item.vat_percentage,
        image_cloudinary_id: resolveToppingImageUrl(
          item.image_cloudinary_id,
          branch
        ),
      }));
    });

    const format = {
      item_id: product.item_id,
      name: `${product.name}`,
      price: product.price,
      image_cloudinary_id: product?.image_cloudinary_id,

      sub_content: [...subContent.flat(1)],
      quantity: quantity,
    };
    // @ts-ignore
    const item: CartItem = {
      ...format,
      parent_id: parseInt(categoryId),
    };
    if (cartItem === undefined) {
      addCartItem(item);
    } else {
      updateCartItem(cartIndex, item);
    }
    setFlyItem((cur) => (cur ? { ...cur, enabled: true } : null));
    history.goBack();
  };

  const onNextClick = () => {
    if (steps === activeIndex + 1) {
      return onSubmit();
    }
    increaseStep();
  };

  const totalToppingsPrice = useMemo(() => {
    let totalSum = parseFloat(product?.price) ?? 0;
    Object.keys(selectedToppings).forEach((key) => {
      const sum = selectedToppings[key].reduce(
        (acc, cur) => acc + parseFloat(cur?.price || "0"),
        0
      );
      totalSum += sum;
    });
    return totalSum;
  }, [selectedToppings, product]);

  const selectedToppingsAsString = useMemo(() => {
    return `${Object.keys(selectedToppings)
      .reverse()
      .map((key) => selectedToppings[key].flatMap((i) => i?.name).join(", "))}`;
  }, [selectedToppings]);

  return (
    <Kiosk>
      <div className="grid h-screen grid-rows-[auto_1fr_auto] overflow-hidden p-12">
        <div>
          <KioskProductDescription
            product={product}
            description={selectedToppingsAsString}
            price={totalToppingsPrice}
          />
          <div className="mt-[2rem]">
            <ProgressBar value={(100 / steps) * activeIndex} />
          </div>
        </div>
        <div className="min-h-0 overflow-hidden">
          <Swiper
            className="h-full"
            allowTouchMove={false} // blokkeert swipe + drag
            onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            slidesPerView={1}
            style={{ height: "100%" }}
          >
            {toppingsCats?.map((garnish, index) => (
              <SwiperSlide
                draggable={false}
                key={garnish.item_id}
                className="!h-full"
              >
                <div className="flex h-full min-h-0 w-full flex-col">
                  <div className="mt-[1rem] flex min-h-0 w-full flex-1 flex-col">
                    <div className="flex shrink-0 items-center justify-center gap-4">
                      <div
                        className="text-center text-[4rem]"
                        style={{ fontWeight: "900", verticalAlign: "center" }}
                      >
                        {getFieldByLang(garnish.name)}{" "}
                      </div>
                      <div
                        className="text-[1.8rem] italic"
                        style={{ fontWeight: "100" }}
                      >
                        {garnish.topping_content_type === 1
                          ? common("required")
                          : common("optional")}
                        {garnish.topping_content_type === 3 &&
                          ` ${common("multipleChoiceAvailable")}`}
                      </div>
                    </div>
                    <ToppingChoice
                      values={selectedToppings}
                      update={setSelectedToppings}
                      garnish={garnish}
                      index={index}
                    />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <div
          id="bottom-buttons"
          className="sticky bottom-0 z-10 flex gap-4 pt-4"
        >
          <div className="w-full">
            <Button
              onClick={onPrevClick}
              variant="secondary"
              size="large"
              className={kioskNavButtonClass}
            >
              {t("goBack")}
            </Button>
          </div>
          <div className="w-full">
            <Button
              isDisabled={isDisabled()}
              onClick={onNextClick}
              size="large"
              className={kioskNavButtonClass}
            >
              {t("next")}
            </Button>
          </div>
        </div>
      </div>
    </Kiosk>
  );
};

export default KioskProductDetail;
